import { cloneDeep } from 'lodash';
import { convertMaterialIdsToMapRecursive, convertMaterialIdsToObjRecursive } from '@/framework';
import { ITextLayerProps, LayerType } from '@/version-types';
import { IClipboardData, IClipboardDataLegacy, IClipboardTemplate } from './types';
import { removeAIPPTExtraInfo } from './utils';

/**
 * 读取剪贴板数据
 * @param e - 剪贴板事件
 * @returns 剪贴板数据
 */
export const readClipboardData = (e?: ClipboardEvent | null): IClipboardData => {
    const clipboardData: IClipboardData = {
        text: '',
        html: '',
        files: [],
        template: null,
    };

    // 系统剪贴板内容优先(兼容 windows 内嵌)
    if (e) {
        // 读取系统的剪贴板的纯文本内容
        if (e.clipboardData && e.clipboardData.getData) {
            // 剔除\r是为了兼容 windows 系统剪贴板内容（\r\n）
            clipboardData.text = e.clipboardData.getData('text/plain')?.replace(/\r/g, '') ?? '';
        } else if ((window as any).clipboardData && (window as any).clipboardData.getData) {
            clipboardData.text = (window as any).clipboardData.getData('Text');
        }

        // 读取系统的剪贴板的 html 内容
        clipboardData.html = e?.clipboardData?.getData('text/html') || '';
        // 读取系统的剪贴板的文件内容
        clipboardData.files = Array.from(e?.clipboardData?.files || []);
    }

    // 读取自定义的剪贴板的图层内容
    try {
        const clipboardTemplateLegacy: IClipboardDataLegacy = JSON.parse(
            window.localStorage.getItem('template_clipboard') || 'null',
        );

        if (!clipboardTemplateLegacy) return clipboardData;

        // 兼容旧版的单舞台数据结构，可以考虑直接删除
        if (
            clipboardTemplateLegacy.template &&
            !Array.isArray(clipboardTemplateLegacy.template) &&
            Array.isArray(clipboardTemplateLegacy.template.layer)
        ) {
            clipboardTemplateLegacy.template = [
                {
                    layer: clipboardTemplateLegacy.template.layer,
                    extraInfo: clipboardTemplateLegacy.template.extraInfo,
                },
            ];
        }
        const clipboardTemplate = clipboardTemplateLegacy as unknown as IClipboardTemplate;

        if (!clipboardTemplate) return clipboardData;

        const flattenLayers = Array.isArray(clipboardTemplate)
            ? clipboardTemplate.map((page) => page.layer).flat()
            : [clipboardTemplate.layer];

        convertMaterialIdsToMapRecursive(flattenLayers);

        clipboardData.template = clipboardTemplate as unknown as IClipboardTemplate;
    } catch (e) {
        console.error('error', e);
    }

    return clipboardData;
};

/**
 * 写入剪贴板数据
 * @param copyTemplate - 拷贝数据
 * @param mutable - 传入的 copyTemplate 是否可直接修改，默认是深拷贝后再修改
 * @param writeSystemClipboard - 是否同时写系统剪贴板；复制事件已同步写入时传 false
 * @returns 序列化后的内部剪贴板数据或纯文本
 */
export const writeClipboardData = (
    copyTemplate: IClipboardTemplate | string,
    mutable = false,
    writeSystemClipboard = true,
) => {
    if (!copyTemplate) return '';

    if (typeof copyTemplate === 'string') {
        if (writeSystemClipboard) navigator.clipboard?.writeText(copyTemplate);
        window.localStorage.removeItem('template_clipboard');
        return copyTemplate;
    }

    let customClipboardData = '';

    // 写入舞台数据到剪贴板
    if (Array.isArray(copyTemplate)) {
        const stages = copyTemplate.map((item, index) => {
            const copyLayers = mutable ? item.layer : cloneDeep(item.layer);
            const extraInfo = item.extraInfo;

            // ATTENTION: JSON stringify 会丢失 Map 类型，需要先转换为对象
            convertMaterialIdsToObjRecursive(copyLayers);

            return {
                layer: copyLayers,
                extraInfo,
            };
        });

        customClipboardData = JSON.stringify(stages);
        if (writeSystemClipboard) navigator.clipboard?.writeText('');
    }
    // 写入图层数据到剪贴板
    else {
        const copyLayer = mutable ? copyTemplate.layer : cloneDeep(copyTemplate.layer);
        const extraInfo = copyTemplate.extraInfo;

        // ATTENTION: 图层复制时，需要取消父图层的关联关系
        copyLayer.parentId = undefined;
        // ATTENTION: JSON stringify 会丢失 Map 类型，需要先转换为对象
        convertMaterialIdsToObjRecursive([copyLayer]);

        if (copyLayer.layerType === LayerType.TEXT_LAYER) {
            if (writeSystemClipboard) navigator.clipboard?.writeText((copyLayer.data as ITextLayerProps).text);
        } else {
            if (writeSystemClipboard) navigator.clipboard?.writeText('');
        }

        removeAIPPTExtraInfo(extraInfo);

        customClipboardData = JSON.stringify({
            layer: copyLayer,
            extraInfo,
        });
    }

    window.localStorage.setItem('template_clipboard', customClipboardData);

    return customClipboardData;
};
